import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { resolveQuotationTemplate } from "../lib/quotationTemplates";
import { Printer, ArrowLeft } from "lucide-react";

const money = (n) =>
    Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n, d = 0) =>
    Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (s) => {
    if (!s) return "";
    try {
        return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Manila" });
    } catch {
        return s;
    }
};

// Gross = the marked-up price the agent set in the cart / SQ (Jenna's "gross price 99.00"),
// NOT the stale unit_price. Prefer the edited given/markup price; fall back to unit_price.
const grossUnit = (l) =>
    Number(l.markup_price ?? l.given_price ?? l.unit_price ?? 0);
// Discounted = the persisted discounted_price when present, else gross x (1 - disc%).
// Discounted Price = the effective per-unit price after ALL discounts (regular + special),
// so a special discount the agent applies is reflected on the client SQ. Uses net_total/qty
// (which carries both discounts); falls back to the stored discounted_price, then gross x (1-disc).
// Discounted Price = after the REGULAR discount only (gross x (1 - disc%)).
const discountedUnit = (l) => {
    if (l.discounted_price != null && l.discounted_price !== "") return Number(l.discounted_price);
    return grossUnit(l) * (1 - Number(l.discount_pct || 0) / 100);
};
// Special Disc. Price = the effective per-unit price after ALL discounts (regular + special),
// i.e. net_total / qty. Equals the Discounted Price when no special discount was applied.
const specialUnit = (l) => {
    const q = Number(l.quantity || 0);
    const net = Number(l.net_total || 0);
    if (q > 0 && net > 0) return net / q;
    return discountedUnit(l);
};
// UAT 10-26: only show a discounted / special price when a discount was actually applied; otherwise
// "—", so the column does not repeat the Gross Price and read as a discount that was never given.
const DISC_EPS = 0.005;
const hasRegularDisc = (l) => discountedUnit(l) < grossUnit(l) - DISC_EPS;
const hasSpecialDisc = (l) => specialUnit(l) < discountedUnit(l) - DISC_EPS;

const availLabel = (l) => {
    const s = l.availability_status || l.allocation_status || l.allocation?.status || "";
    if (["available", "available_in_x_batches"].includes(s)) return { t: "Available", c: "#2f7d4f" };
    if (s === "not_enough") return { t: "Partial", c: "#b06a12" };
    if (s === "without_stock") return { t: "Indent 30–60d", c: "#b06a12" };
    return { t: s || "—", c: "#555" };
};

const COLUMNS = {
    no: { label: "Item No.", align: "c", cell: (l, i) => i + 1 },
    description: { label: "Description", align: "", cell: (l) => (
        <>
            {l.item_name || l.description || "—"}
            {l.description && l.description !== l.item_name
                ? <div style={{ fontSize: "9px", color: "#555", lineHeight: 1.2 }}>{l.description}</div>
                : null}
        </>
    ) },
    item_code: { label: "Item Code", align: "", cell: (l) => l.item_code || "—" },
    size: { label: "Size (cm)", align: "c", cell: (l) => l.size || "—" },
    qty: { label: "Quantity", align: "n", cell: (l) => num(l.quantity) },
    boxes: { label: "Box'es", align: "n", cell: (l) => num(l.estimated_boxes, 2) },
    weight: { label: "Weight/pc (Kg)", align: "n", cell: (l) => num(l.weight_per_pc, 2) },
    availability: {
        label: "Availability",
        align: "c",
        cell: (l) => {
            const a = availLabel(l);
            return <span style={{ color: a.c, fontWeight: 700 }}>{a.t}</span>;
        },
    },
    unit_price: { label: "Gross Price", align: "n", cell: (l) => money(grossUnit(l)) },
    srp: { label: "Gross Price", align: "n", cell: (l) => money(grossUnit(l)) },
    gross_price: { label: "Gross Price", align: "n", cell: (l) => money(grossUnit(l)) },
    remarks: {
        label: "Remarks",
        align: "c",
        cell: (l) => {
            const a = availLabel(l);
            return <span style={{ color: a.c, fontWeight: 700 }}>{a.t}</span>;
        },
    },
    discounted: { label: "Discounted Price", align: "n", cell: (l) => hasRegularDisc(l) ? money(discountedUnit(l)) : "—" },
    special: { label: "Special Disc. Price", align: "n", cell: (l) => hasSpecialDisc(l) ? money(specialUnit(l)) : "—" },
    area: { label: "Area", align: "", cell: (l) => l.area || "—" },
    net_total: { label: "Net Total", align: "n", cell: (l) => money(l.net_total) },
};

const METok = {
    date: (q) => ["Date", fmtDate(q.created_at)],
    quotation_no: (q) => ["Quotation No.", q.doc_no || "—"],
    ref: (q) => ["Ref", q.ref || q.reference || "—"],
    to: (q) => ["To", q.customer_snapshot?.company_name || q.end_customer_name || q.customer_name || "—"],
    contact: (q) => ["Contact No.", q.customer_snapshot?.contact_no || q.customer_contact_no || "—"],
    thru: (q) => ["Thru", q.customer_snapshot?.contact_person || q.customer_contact_person || "—"],
    project: (q) => ["Project", q.project || q.project_name || "—"],
    project_address: (q) => ["Project Address", q.project_address || q.customer_snapshot?.address || "—"],
    // "Location" on an SQ is the PROJECT location (Jenna), not the customer's office address.
    location: (q) => ["Location", q.project_address || q.project_location || q.customer_snapshot?.address || "—"],
    agent: (q) => ["Agent", q.agent || q.user_email || "—"],
    company: (q) => ["Company", q.customer_snapshot?.company_name || q.end_customer_name || q.customer_name || "—"],
    company_address: (q) => ["Company Address", q.customer_snapshot?.address || q.customer_address || "—"],
    tin: (q) => ["TIN", q.customer_snapshot?.tin || q.customer_tin || "—"],
    delivery_address: (q) => ["Delivery Address", q.delivery_address || q.customer_snapshot?.address || "—"],
};

export default function QuotationPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [q, setQ] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/quotations/${id}`);
                setQ(data);
            } catch (e) {
                setErr(formatApiError(e));
            }
        })();
    }, [id]);

    const tpl = useMemo(() => {
        if (!q) return null;
        // Letterhead follows the SELLING company (quotation owner's company/branch),
        // not the supplier schema the stock came from.
        return resolveQuotationTemplate({ company: q.company, branch: q.branch, displayName: q.company });
    }, [q]);

    const totals = useMemo(() => {
        const lines = (q && q.lines) || [];
        const gross = lines.reduce((a, l) => a + grossUnit(l) * Number(l.quantity || 0), 0);
        const net = q?.subtotal ?? lines.reduce((a, l) => a + Number(l.net_total || 0), 0);
        const otherCharges = (q?.other_charges || []).reduce((a, c) => a + Number(c.amount || 0), 0);
        const delivery = Number(q?.delivery_charge || 0);
        return { gross, net, delivery, otherCharges, total: net + delivery + otherCharges };
    }, [q]);

    if (err) return <div style={{ padding: 40, fontFamily: "system-ui" }}>Could not load quotation: {err}</div>;
    if (!q || !tpl) return <div style={{ padding: 40, fontFamily: "system-ui" }}>Loading quotation…</div>;

    const co = tpl.company;
    const _colKeys = [...tpl.columns];
    // R52: Area column (after Size/Description) if the template omits it.
    if (!_colKeys.includes("area")) {
        const _di = Math.max(_colKeys.indexOf("size"), _colKeys.indexOf("description"));
        _colKeys.splice(_di >= 0 ? _di + 1 : 1, 0, "area");
    }
    // R51: distinct Special-Discount price column right after Discounted Price.
    if (!_colKeys.includes("special")) {
        const _pi = _colKeys.indexOf("discounted");
        if (_pi >= 0) _colKeys.splice(_pi + 1, 0, "special");
    }
    const cols = _colKeys.map((k) => COLUMNS[k]).filter(Boolean);
    // Edited T&C (from the quotation phase) override the template default.
    const terms = q.terms_override && q.terms_override.length ? q.terms_override : tpl.terms;
    // Per-quotation Important Reminders override the template's default list.
    const reminders = q.reminders_override && q.reminders_override.length ? q.reminders_override : (tpl.reminders || []);
    // Payment box: print the entered value, else the blank fill-in line.
    const payAmt = (v) => (v != null && v !== "" ? `₱ ${money(v)}` : "₱ ______________");
    const payTxt = (v) => (v ? fmtDate(v) : "______________");
    const showPay = tpl.payment || q.pay_fully_paid != null || q.pay_partial != null || q.pay_balance != null || !!q.pay_tentative_date;

    return (
        <div className="qp-root">
            <style>{CSS}</style>

            <div className="qp-bar">
                <button className="qp-btn ghost" onClick={() => navigate(`/quotations/${id}`)}>
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="qp-bar-title">Print preview · {q.doc_no}</div>
                <button className="qp-btn" onClick={() => window.print()}>
                    <Printer size={16} /> Print / Save as PDF
                </button>
            </div>

            <div className="sheet">
                <div className="hdr">
                    {co.logo ? (
                        <img src={co.logo} alt={co.name} className={co.logoDark ? "logo dark" : "logo"} />
                    ) : co.wordmark ? (
                        <div className="wordmark">
                            {co.wordmark.text}
                            <small>{co.wordmark.tagline}</small>
                        </div>
                    ) : (
                        <div className="wordmark">{co.name}</div>
                    )}
                    <div className="co">
                        <b>{co.name}</b>
                        {(co.address || []).map((a, i) => (
                            <div key={i}>{a}</div>
                        ))}
                    </div>
                </div>

                <div className="doc-title">SALES QUOTATION</div>

                <div className="meta">
                    {tpl.meta.map((k) => {
                        const fn = METok[k];
                        if (!fn) return null;
                        const [label, value] = fn(q);
                        return (
                            <div className="row" key={k}>
                                <span className="k">{label}</span>
                                <span className="v">{value}</span>
                            </div>
                        );
                    })}
                    {/* Always surface project name / address on the client SQ when set, even if
                        this company's template meta list omits them (Jenna: they weren't reflecting). */}
                    {q.project_name && !tpl.meta.includes("project") && (
                        <div className="row" key="x-project">
                            <span className="k">Project</span>
                            <span className="v">{q.project_name}</span>
                        </div>
                    )}
                    {q.project_address && !tpl.meta.includes("project_address") && !tpl.meta.includes("location") && (
                        <div className="row" key="x-project-address">
                            <span className="k">Project Address</span>
                            <span className="v">{q.project_address}</span>
                        </div>
                    )}
                </div>

                <p className="intro">
                    {tpl.intro ||
                        "We are pleased to submit herewith our quotation for your project requirements. Specification, terms and conditions are listed below."}
                </p>

                {tpl.adhesive && <div className="secttag">SECTION A · ITEMS</div>}
                <div className="tablewrap">
                    <table className="items">
                        <thead>
                            <tr>
                                {cols.map((c, i) => (
                                    <th key={i} className={c.align}>
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(q.lines || []).map((l, i) => (
                                <tr key={l.item_code + i}>
                                    {cols.map((c, k) => (
                                        <td key={k} className={c.align}>
                                            {c.cell(l, i)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {tpl.adhesive && (q.adhesive_lines || []).length > 0 && (
                    <>
                        <div className="secttag">SECTION B · ADHESIVE / GROUT</div>
                        <div className="tablewrap">
                            <table className="items">
                                <thead>
                                    <tr>
                                        <th>Adhesive / Grout</th>
                                        <th>Code</th>
                                        <th className="c">Unit</th>
                                        <th className="n">Qty</th>
                                        <th className="n">SRP per pc</th>
                                        <th className="n">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {q.adhesive_lines.map((a, i) => (
                                        <tr key={i}>
                                            <td>{a.description}</td>
                                            <td>{a.code}</td>
                                            <td className="c">{a.unit}</td>
                                            <td className="n">{num(a.quantity)}</td>
                                            <td className="n">{money(a.srp)}</td>
                                            <td className="n">{money(a.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tpl.totals === "amount" ? (
                    <div className="totals">
                        {(q.other_charges || []).map((c, i) => (
                            <div className="tr" key={i}>
                                <span>{c.label || c.type || "Other Charge"}</span>
                                <span className="v">₱ {money(c.amount)}</span>
                            </div>
                        ))}
                        <div className="tr grand">
                            <span>TOTAL AMOUNT</span>
                            <span className="v">₱ {money(totals.total)}</span>
                        </div>
                    </div>
                ) : tpl.totals === "split" ? (
                    <div className="totals">
                        <div className="tr">
                            <span>Total Gross Items (A)</span>
                            <span className="v">₱ {money(totals.gross)}</span>
                        </div>
                        <div className="tr">
                            <span>Total Discounted Items (A)</span>
                            <span className="v">₱ {money(totals.net)}</span>
                        </div>
                        <div className="tr">
                            <span>Adhesive / Grout (B)</span>
                            <span className="v">₱ {money(q.adhesive_total || 0)}</span>
                        </div>
                        {(q.other_charges || []).map((c, i) => (
                            <div className="tr" key={i}>
                                <span>{c.label || c.type || "Other Charge"}</span>
                                <span className="v">₱ {money(c.amount)}</span>
                            </div>
                        ))}
                        {totals.delivery > 0 && (
                            <div className="tr">
                                <span>Delivery Charge</span>
                                <span className="v">₱ {money(totals.delivery)}</span>
                            </div>
                        )}
                        <div className="tr grand">
                            <span>TOTAL AMOUNT</span>
                            <span className="v">₱ {money(totals.total + Number(q.adhesive_total || 0))}</span>
                        </div>
                    </div>
                ) : (
                    <div className="totals">
                        {(q.other_charges || []).map((c, i) => (
                            <div className="tr" key={i}>
                                <span>{c.label || c.type || "Other Charge"}</span>
                                <span className="v">₱ {money(c.amount)}</span>
                            </div>
                        ))}
                        {totals.delivery > 0 && (
                            <div className="tr">
                                <span>Delivery Charge</span>
                                <span className="v">₱ {money(totals.delivery)}</span>
                            </div>
                        )}
                        <div className="tr grand">
                            <span>TOTAL</span>
                            <span className="v">₱ {money(totals.total)}</span>
                        </div>
                    </div>
                )}

                {showPay && (
                    <div className="paybox">
                        <div className="pr">
                            <span>Fully Paid Amount (no balance)</span>
                            <b>{payAmt(q.pay_fully_paid)}</b>
                        </div>
                        <div className="pr">
                            <span>Partial</span>
                            <b>{payAmt(q.pay_partial)}</b>
                        </div>
                        <div className="pr">
                            <span>Balance</span>
                            <b>{payAmt(q.pay_balance)}</b>
                        </div>
                        <div className="pr">
                            <span>Tentative full-payment date of the balance</span>
                            <b>{payTxt(q.pay_tentative_date)}</b>
                        </div>
                    </div>
                )}

                {reminders.length > 0 && (
                    <div className="terms">
                        <h4>Important Reminders</h4>
                        <ol>
                            {reminders.map((t, i) => (
                                <li key={i}>{t}</li>
                            ))}
                        </ol>
                    </div>
                )}

                <div className="terms">
                    <h4>Terms &amp; Conditions</h4>
                    <ol>
                        {terms.map((t, i) => (
                            <li key={i}>{t}</li>
                        ))}
                    </ol>
                </div>

                <div className="sigblock">
                    <div className="sig-col">
                        <div className="line"></div>
                        <b>{q.agent || q.user_email || ""}</b>
                        <span>Prepared by</span>
                        <span className="sub">Signature over printed name</span>
                    </div>
                    <div className="sig-col">
                        <div className="line"></div>
                        <b>&nbsp;</b>
                        <span>Reviewed by / Noted by</span>
                        <span className="sub">Signature over printed name</span>
                    </div>
                    <div className="sig-col">
                        <div className="line"></div>
                        <b>{q.customer_snapshot?.company_name || q.end_customer_name || q.customer_name || ""}</b>
                        <span>Conforme (Client)</span>
                        <span className="sub">Signature over printed name</span>
                    </div>
                </div>

                {tpl.signatories && tpl.signatories.length > 0 && (
                    <div className="sig">
                        {tpl.signatories.map((s, i) => (
                            <div className="s" key={i}>
                                <div className="line"></div>
                                <b>{(s.role === "Prepared by" ? (q.agent || q.user_email) : s.name) || " "}</b>
                                <span>
                                    {s.title}
                                    {s.role ? ` · ${s.role}` : ""}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {tpl.closing ? (
                    <p className="intro" style={{ marginTop: 16, fontStyle: "normal" }}>{tpl.closing}</p>
                ) : null}

                {q.notes ? <p className="intro" style={{ marginTop: 16 }}>Notes: {q.notes}</p> : null}
            </div>
        </div>
    );
}

const CSS = `
.qp-root{background:#e7e9ec;min-height:100vh;padding:0 0 60px;font-family:Arial,Helvetica,sans-serif;color:#16181c}
.qp-bar{position:sticky;top:0;z-index:5;display:flex;align-items:center;justify-content:space-between;gap:12px;
  background:#1b2430;color:#fff;padding:10px 16px;box-shadow:0 2px 8px rgba(0,0,0,.2)}
.qp-bar-title{font-size:13px;opacity:.85;font-family:system-ui,sans-serif}
.qp-btn{display:inline-flex;align-items:center;gap:7px;background:#f0b429;color:#1b2430;border:none;border-radius:8px;
  padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;font-family:system-ui,sans-serif}
.qp-btn.ghost{background:transparent;color:#fff;border:1px solid rgba(255,255,255,.3)}
.qp-btn:hover{opacity:.92}

.sheet{max-width:860px;margin:22px auto;background:#fff;border:1px solid #c9ced4;border-radius:4px;
  box-shadow:0 10px 30px rgba(20,30,40,.14);padding:34px 34px 28px;font-size:12px}
.hdr{display:flex;align-items:center;justify-content:space-between;gap:18px;border-bottom:2px solid #16181c;padding-bottom:12px}
.hdr .logo{height:48px;width:auto;display:block}
.hdr .logo.dark{background:#16181c;padding:6px 10px;border-radius:4px}
.co{font-size:11px;color:#444;text-align:right;line-height:1.4}
.co b{display:block;color:#16181c;font-size:13px;letter-spacing:.02em;margin-bottom:2px}
.wordmark{font-family:system-ui,Arial,sans-serif;font-weight:800;letter-spacing:.02em;font-size:28px;color:#16181c;line-height:1}
.wordmark small{display:block;font-size:9px;letter-spacing:.3em;font-weight:600;color:#7a8189;margin-top:4px}
.doc-title{text-align:center;font-weight:700;letter-spacing:.2em;font-size:13px;margin:14px 0;color:#16181c}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:1px 26px;margin-bottom:14px}
.meta .row{display:flex;gap:8px;border-bottom:1px dotted #b9bec4;padding:3px 0}
.meta .row .k{color:#555;min-width:120px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;font-size:9.5px;align-self:center}
.meta .row .v{color:#16181c;flex:1;font-size:11px}
.intro{font-size:10.5px;color:#555;font-style:italic;margin:0 0 8px}
.secttag{display:inline-block;background:#16181c;color:#fff;font-size:9px;font-weight:700;padding:2px 8px;border-radius:3px;letter-spacing:.08em;margin:10px 0 5px}
.tablewrap{overflow-x:auto}
table.items{width:100%;border-collapse:collapse;margin-bottom:4px;font-size:11px}
table.items th{background:#16181c;color:#fff;font-weight:600;font-size:9px;letter-spacing:.02em;text-transform:uppercase;padding:6px;text-align:left}
table.items td{border:1px solid #ccd0d5;padding:6px;vertical-align:top}
table.items .n{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
table.items .c{text-align:center}
table.items tbody tr:nth-child(even){background:#f6f7f9}
.totals{margin-top:10px;margin-left:auto;width:min(340px,100%)}
.totals .tr{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dotted #c3c8ce;font-size:11px}
.totals .tr .v{font-variant-numeric:tabular-nums}
.totals .tr.grand{border-top:2px solid #16181c;border-bottom:none;font-weight:700;font-size:13px;margin-top:3px;padding-top:6px}
.paybox{margin-top:14px;border:1px solid #c9ced4;border-radius:4px;padding:10px 12px;background:#fbfcfd;font-size:10.5px;display:grid;gap:6px}
.paybox .pr{display:flex;justify-content:space-between;border-bottom:1px dotted #c3c8ce;padding-bottom:4px}
.paybox .pr:last-child{border-bottom:none}
.terms{margin-top:16px;border-top:1px solid #d7dce1;padding-top:10px}
.terms h4{margin:0 0 6px;font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#555}
.terms ol{margin:0;padding-left:18px;color:#4a4f55;font-size:9.6px;line-height:1.5}
.terms li{margin-bottom:4px}
.sigblock{margin-top:30px;display:flex;gap:26px;justify-content:space-between}
.sigblock .sig-col{flex:1;text-align:center}
.sigblock .sig-col .line{border-top:1px solid #16181c;margin:36px 6px 4px}
.sigblock .sig-col b{display:block;font-size:10.5px;color:#16181c;min-height:13px}
.sigblock .sig-col span{display:block;font-size:9.4px;color:#4a4f55}
.sigblock .sig-col span.sub{color:#8a8f95;font-size:8.6px}
.sig{display:flex;gap:26px;flex-wrap:wrap;margin-top:24px}
.sig .s{flex:1;min-width:150px;text-align:center;font-size:10px}
.sig .s .line{border-top:1px solid #16181c;margin:26px 0 4px;padding-top:4px}
.sig .s b{display:block;font-size:11px}
.sig .s span{color:#666}

@media print{
  .qp-root{background:#fff;padding:0}
  .qp-bar{display:none}
  .sheet{box-shadow:none;border:none;margin:0;max-width:none;padding:12px}
  @page{margin:12mm}
}
`;
